import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@shared/lib/cn';

export type DropdownTone = 'default' | 'danger';

export interface DropdownProps extends SelectHTMLAttributes<HTMLSelectElement> {
  tone?: DropdownTone;
  showChevron?: boolean;
}

export function dropdownClassName({
  tone = 'default',
  className,
}: {
  tone?: DropdownTone;
  className?: string;
} = {}): string {
  return cn(
    'w-full rounded-md border border-hairline-strong bg-elevated text-body text-ink',
    'placeholder:text-ink-muted focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10',
    'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted',
    'h-12 appearance-none px-3.5 pr-10',
    tone === 'danger' && 'border-danger focus:border-danger focus:ring-danger/10',
    className,
  );
}

/**
 * Native select styled for the resident app — shared by form fields,
 * onboarding flows, and calendar month/year pickers.
 */
export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(function Dropdown(
  { tone = 'default', showChevron = true, className, children, ...selectProps },
  ref,
) {
  return (
    <div className="relative">
      <select ref={ref} className={dropdownClassName({ tone, className })} {...selectProps}>
        {children}
      </select>
      {showChevron ? (
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink"
          strokeWidth={2}
          aria-hidden
        />
      ) : null}
    </div>
  );
});

export interface DropdownOption<T extends string = string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}
